import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkStringify from 'remark-stringify';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import remarkFrontmatter from 'remark-frontmatter';
import { parseFrontmatter, serializeFrontmatter } from '@staleless/schema';
const parser = unified()
    .use(remarkParse)
    .use(remarkFrontmatter, ['yaml'])
    .use(remarkGfm)
    .use(remarkDirective);
/**
 * Escape a `~` that touches another `~`, and only then.
 *
 * Without this the round trip DIVERGES on text holding a run of tildes inside
 * strikethrough — it does not oscillate, it grows: `canonicalise` was measured
 * adding two tildes on every pass, for ever, until it gave up and threw. Since
 * canonicalisation runs on every write, that is a SAVE that fails, on a
 * document a person can type.
 *
 * The mechanism is a feedback loop between the mark and the text. A text node
 * holding `[~~~~` under a strike mark serializes as `~~[~~~~…~~`; on the way
 * back in, the parser takes two of those tildes for the delimiter and leaves
 * the rest in the text, so the text is two tildes longer than it started, and
 * the next pass does it again. Escaping breaks the loop: `\~\~` cannot be read
 * as a delimiter, so the text that comes back is the text that went out.
 *
 * Only when adjacent to another tilde, because a lone one is ordinary writing —
 * "about ~5 minutes" — and escaping every tilde in the repository would rewrite
 * every file that contains one, which the note below rightly calls a migration.
 *
 * Added as a plugin rather than an `extensions` option: remark-stringify
 * IGNORES `extensions` passed to it — measured, with a rule that escaped the
 * letter q and did nothing — and `this.data('toMarkdownExtensions')` is the
 * route it does read.
 */
const LABELLED = [
    'phrasing',
    // A directive LABEL is its own construct, and the escaping that applies to
    // ordinary phrasing does not reach inside it — which is where the loop lives.
    'label',
    'containerDirectiveLabel',
    'textDirectiveLabel',
    'leafDirectiveLabel',
];
const remarkEscapeTildeRuns = function () {
    const data = this.data();
    const extensions = (data.toMarkdownExtensions ??= []);
    extensions.push({
        unsafe: [
            { character: '~', inConstruct: LABELLED, before: '~' },
            { character: '~', inConstruct: LABELLED, after: '~' },
        ],
    });
};
/**
 * Serializer options are pinned deliberately. Any change here rewrites every
 * file in the content repo on its next save, so treat these as a format version:
 * changing one is a migration, not a preference.
 */
const serializer = unified()
    .use(remarkStringify, {
    bullet: '-',
    emphasis: '_',
    strong: '*',
    fence: '`',
    fences: true,
    listItemIndent: 'one',
    rule: '-',
    ruleRepetition: 3,
    tightDefinitions: true,
    // Escape as little as possible: over-escaping is what makes a round trip
    // visibly churn a file on every save.
    resourceLink: false,
})
    .use(remarkFrontmatter, ['yaml'])
    .use(remarkGfm)
    .use(remarkDirective)
    // On the SERIALIZER, which is the only chain that escapes anything. Both
    // chains end with the same three plugins, and putting this on the parser by
    // mistake changed nothing at all — quietly, because a parse pipeline simply
    // never asks about unsafe characters.
    .use(remarkEscapeTildeRuns);
export function parseDocument(markdown) {
    const tree = parser.parse(markdown);
    const processed = parser.runSync(tree);
    let frontmatterYaml = '';
    const children = processed.children.filter((node) => {
        if (node.type === 'yaml') {
            frontmatterYaml = node.value;
            return false;
        }
        return true;
    });
    return {
        frontmatter: parseFrontmatter(frontmatterYaml),
        body: { type: 'root', children },
        ...(frontmatterYaml ? { frontmatterText: frontmatterYaml } : {}),
    };
}
export function serializeDocument(doc) {
    // remark-stringify supplies the `---` fences. Adding newlines here puts a
    // blank line inside them, which reparses fine but churns every file.
    const frontmatter = serializeFrontmatter(doc.frontmatter, doc.frontmatterText);
    /**
     * No fence at all when there is nothing to put in it.
     *
     * A page with no frontmatter used to come back with `---\n{}\n---` on top:
     * the serializer always wrote the node, and an empty mapping stringifies to
     * `{}`. See `serializeFrontmatter`, where the other half of this lives.
     */
    const tree = {
        type: 'root',
        children: [
            ...(frontmatter.trim() === ''
                ? []
                : [{ type: 'yaml', value: frontmatter }]),
            ...doc.body.children,
        ],
    };
    return serializer.stringify(tree);
}
/** Body-only helpers, for fragments that carry no frontmatter. */
export function parseBody(markdown) {
    return parser.runSync(parser.parse(markdown));
}
export function serializeBody(body) {
    return serializer.stringify(body);
}
//# sourceMappingURL=pipeline.js.map